const MAX_SUGGESTED_PROMPTS = 3
const MAX_FOLLOW_UPS = 3

// Every prompt must map to a real capability — a business tool, filtered
// navigation, promotion draft, or help-article search.
const DEFAULT_PROMPTS = [
	'What needs my attention today?',
	'What were my top products last week?',
	'Plan my store visits for today',
]

const OVERVIEW_PROMPTS = [
	'What needs my attention today?',
	'How did this week compare to last week?',
	'Which cabinets need restocking?',
]

const PAGE_PROMPTS: Record<string, string[]> = {
	'/dashboard/health': [
		'Which cabinets need attention right now?',
		'Which products are out of stock?',
		'Any failed payments this week?',
	],
	'/dashboard/stores': [
		'Which store had the most revenue this week?',
		'Are any stores running low on stock?',
		'Compare my top and bottom stores this month',
	],
	'/dashboard/products': [
		'What are my top selling products?',
		'Which products are bought together most often?',
		'Which products are underperforming?',
	],
	'/dashboard/transactions': [
		'Show me failed transactions today',
		'Why are payments failing this week?',
		'What is my average transaction value?',
	],
	'/dashboard/analytics': [
		'Give me a revenue summary for this week',
		'What are the peak hours across stores?',
		'How does this week compare to last week?',
	],
	'/dashboard/inventory': [
		'Which products are out of stock right now?',
		'How full are my cabinets?',
		'Which cabinets are running lowest on stock?',
	],
	'/dashboard/restocks': [
		'Which cabinets are overdue for a restock?',
		'What should I restock first today?',
		'Plan my restocking route',
	],
	'/dashboard/planograms': [
		'How does the planogram approval workflow work?',
		'Which products are bought together most often?',
		'What are my top selling products?',
	],
	'/dashboard/promotions': [
		'How are my promotions performing?',
		'Create a promotion for a product',
		'What discounts were redeemed this month?',
	],
	'/dashboard/finances': ['How do payouts work?', 'When do I get paid?', 'Give me a revenue summary for this month'],
	'/dashboard/market-insights': [
		'Which products are underperforming?',
		'What are my top selling products?',
		'Which products are bought together most often?',
	],
	'/dashboard/marketing': [
		'How do I create marketing content for my stores?',
		'How do I upload a video for my smart store?',
	],
	'/dashboard/settings': [
		'How do I set up taxes?',
		'How do I invite a team member?',
		'How do I get help with an issue?',
	],
}

const ENTITY_PROMPTS: Record<string, string[]> = {
	cabinet: [
		'What should I restock first in this cabinet?',
		'Show me a sales heatmap for this cabinet',
		'Which products pair well together? Show them on the map',
	],
	cabinetPending: [
		'Summarize the pending planogram changes',
		'Show me vision risks on the pending layout',
		'Which products pair well together? Show them on the map',
	],
	store: [
		'What needs my attention at this store?',
		'Compare cabinets at this store',
		'Plan my visit — which cabinets first?',
	],
	product: [
		'How is this product performing?',
		'Where should this product be placed?',
		'Which products pair well with this one?',
	],
	planogram: [
		'Suggest planogram swaps and preview them on the map',
		'Show me a sales heatmap for this cabinet',
		'Which products pair well together? Show them on the map',
	],
}

const CABINET_PATH = /\/cabinets\/[^/]+/
const PLANOGRAM_PATH = /\/planogram\/\d+/
const DASHBOARD_STORE_PATH = /\/dashboard\/stores\/\d+/
const ANY_STORE_PATH = /\/stores\/\d+/

export function getSuggestedPrompts(pathname: string, search?: string): string[] {
	const params = new URLSearchParams(search ?? '')
	let prompts: string[]
	if (pathname.startsWith('/dashboard/restocks')) {
		// Restock flow paths contain /cabinets/ but carry no cabinet context.
		prompts = PAGE_PROMPTS['/dashboard/restocks']
	} else if (PLANOGRAM_PATH.test(pathname)) {
		prompts = ENTITY_PROMPTS.planogram
	} else if (CABINET_PATH.test(pathname)) {
		prompts = params.get('type') === 'pending' ? ENTITY_PROMPTS.cabinetPending : ENTITY_PROMPTS.cabinet
	} else if (DASHBOARD_STORE_PATH.test(pathname)) {
		prompts = ENTITY_PROMPTS.store
	} else if (/\/dashboard\/products\/\d+/.test(pathname)) {
		prompts = ENTITY_PROMPTS.product
	} else if (/\/dashboard\/products\/?$/.test(pathname) && params.get('preview_id')) {
		prompts = ENTITY_PROMPTS.product
	} else if (pathname === '/dashboard' || pathname === '/dashboard/' || pathname === '/') {
		prompts = OVERVIEW_PROMPTS
	} else {
		const match = Object.keys(PAGE_PROMPTS).find((prefix) => pathname.startsWith(prefix))
		prompts = match ? PAGE_PROMPTS[match] : DEFAULT_PROMPTS
	}
	return prompts.slice(0, MAX_SUGGESTED_PROMPTS)
}

export function getFollowUpSuggestions(
	lastContent: string,
	pathname: string,
	toolCalls?: { name: string; status: string }[]
): string[] {
	if (!lastContent) return []
	const suggestions: string[] = []
	const add = (text: string) => {
		if (!suggestions.includes(text)) suggestions.push(text)
	}
	// Only the tools that actually ran drive follow-ups — keyword-matching the
	// answer text made generic chips appear after nearly every message.
	const toolNames = new Set(toolCalls?.filter((tc) => tc.status === 'done').map((tc) => tc.name) ?? [])
	if (!toolNames.size) return []

	if (toolNames.has('get_sales_summary')) {
		add('Compare to last period')
		add('Break down by day')
	}
	if (toolNames.has('get_product_ranking')) {
		add('Show me the worst performers')
		add('Which products are out of stock?')
	}
	if (toolNames.has('get_store_ranking')) {
		add('Show trends over the last month')
	}
	if (toolNames.has('get_row_performance')) {
		add('Which shelf sells the most?')
		add('Suggest planogram swaps')
	}
	if (toolNames.has('get_cell_performance') && !toolNames.has('get_row_performance')) {
		add('Compare top vs bottom shelf')
	}
	const onCabinetPage = CABINET_PATH.test(pathname) || PLANOGRAM_PATH.test(pathname)
	if (toolNames.has('get_ops_digest')) {
		add('Show cabinet issues on the map')
	}
	if (toolNames.has('get_planogram_layout') && onCabinetPage && !toolNames.has('get_cabinet_slot_inventory')) {
		add('What should I restock first in this cabinet?')
	}
	if (toolNames.has('suggest_planogram_swaps') && onCabinetPage) {
		add('Preview swaps on the cabinet map')
	}
	if (onCabinetPage) {
		if (toolNames.has('get_cell_performance') && !toolNames.has('get_planogram_layout')) {
			add('Show this as a cabinet heatmap')
		}
		if (
			(toolNames.has('get_product_affinity') || toolNames.has('get_affinity_placement')) &&
			!toolNames.has('get_planogram_layout')
		) {
			add('Show pairs on the cabinet map')
		}
		if (
			toolNames.has('get_cell_performance') &&
			!toolNames.has('get_affinity_placement') &&
			!toolNames.has('get_product_affinity')
		) {
			add('Show product pairs on the cabinet map')
		}
		if (
			(toolNames.has('get_product_affinity') || toolNames.has('get_affinity_placement')) &&
			!toolNames.has('get_cell_performance')
		) {
			add('Show me a sales heatmap for this cabinet')
		}
	}
	if (toolNames.has('get_restock_status')) {
		add('Which items need restocking urgently?')
	}
	if (toolNames.has('get_product_affinity') || toolNames.has('get_affinity_placement')) {
		add('Should we move these pairs closer?')
		add('Which high-lift pairs are far apart?')
		if (DASHBOARD_STORE_PATH.test(pathname)) {
			add('Which product pairs sit far apart on the shelf?')
		}
	}
	if (toolNames.has('prepare_promotion_draft')) {
		add('Create another promotion')
	}
	if (CABINET_PATH.test(pathname)) {
		add('When was the last restock?')
	}
	if (ANY_STORE_PATH.test(pathname)) {
		add('Show cabinet breakdown')
	}

	return suggestions.slice(0, MAX_FOLLOW_UPS)
}
